/**
 * Danish Number Formatting Utilities
 * Ensures Excel compatibility with Danish regional settings
 */

/**
 * Converts a number to Danish format with comma as decimal separator
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted number like "1.234,56"
 */
export const toDanishNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0' + (decimals > 0 ? ',' + '0'.repeat(decimals) : '');
  }

  // Round to specified decimals
  const factor = Math.pow(10, decimals);
  const rounded = Math.round(value * factor) / factor;

  // Convert to string with fixed decimals
  const parts = Math.abs(rounded).toFixed(decimals).split('.');
  
  // Add thousand separators (period in Danish)
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  // Join with comma as decimal separator
  const formatted = parts.join(',');
  
  // Add negative sign if needed
  return (rounded < 0 ? '-' : '') + formatted;
};

/**
 * Parses a Danish formatted number string to float
 * @param {string} danishString - String like "1.234,56"
 * @returns {number} - Parsed number
 */
export const fromDanishNumber = (danishString) => {
  if (!danishString || typeof danishString !== 'string') {
    return 0;
  }

  // Remove thousand separators (periods)
  // Replace decimal separator (comma) with period
  const normalized = danishString
    .replace(/\./g, '')
    .replace(',', '.');

  return parseFloat(normalized) || 0;
};

/**
 * Formats a number as Danish currency
 * @param {number} value - The amount
 * @param {string} currency - Currency code (default: 'DKK')
 * @param {number} decimals - Decimal places (default: 2)
 * @returns {string} - Formatted currency like "1.234,56 DKK"
 */
export const formatCurrency = (value, currency = 'DKK', decimals = 2) => {
  const formatted = toDanishNumber(value, decimals);
  return `${formatted} ${currency}`;
};

/**
 * Formats a number as Danish percentage
 * @param {number} value - The percentage value (e.g., 95.3 for 95.3%)
 * @param {number} decimals - Decimal places (default: 1)
 * @returns {string} - Formatted percentage like "95,3%"
 */
export const formatPercentage = (value, decimals = 1) => {
  const formatted = toDanishNumber(value, decimals);
  return `${formatted}%`;
};

/**
 * Formats a large number with Danish thousand separators
 * Useful for displaying large integers
 * @param {number} value - The number to format
 * @returns {string} - Formatted number like "1.234.567"
 */
export const formatLargeNumber = (value) => {
  return toDanishNumber(value, 0);
};

/**
 * Formats energy values (MWh, GWh) with appropriate unit
 * @param {number} value - Energy in MWh
 * @param {number} decimals - Decimal places (default: 2)
 * @returns {string} - Formatted energy like "1,23 GWh" or "456,78 MWh"
 */
export const formatEnergy = (value, decimals = 2) => {
  if (Math.abs(value) >= 1000) {
    return `${toDanishNumber(value / 1000, decimals)} GWh`;
  }
  return `${toDanishNumber(value, decimals)} MWh`;
};

/**
 * Formats power values (MW, kW) with appropriate unit
 * @param {number} value - Power in MW
 * @param {number} decimals - Decimal places (default: 1)
 * @returns {string} - Formatted power like "12,5 MW" or "450,0 kW"
 */
export const formatPower = (value, decimals = 1) => {
  if (Math.abs(value) < 1) {
    return `${toDanishNumber(value * 1000, decimals)} kW`;
  }
  return `${toDanishNumber(value, decimals)} MW`;
};

export default {
  toDanishNumber,
  fromDanishNumber,
  formatCurrency,
  formatPercentage,
  formatLargeNumber,
  formatEnergy,
  formatPower,
};
